<template>
  <div>
    <div ref="map" style="height: 400px; width: 100%;" class="mt-2 rounded border"></div>
  </div>
</template>

<script>
export default {
  name: "PlacesAutocomplete",
  props: {
    inputId: {
      type: String,
      required: true
    },
    initialPosition: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      map: null,
      marker: null,
      geocoder: null,
      autocomplete: null,
    };
  },
  mounted() {
    // Initialization will be triggered by the parent component
  },
  methods: {
    initialize() {
      const checkInterval = 100; // ms
      const timeout = 5000; // 5 seconds
      let elapsedTime = 0;

      const intervalId = setInterval(() => {
        const inputElement = document.getElementById(this.inputId);
        elapsedTime += checkInterval;

        if (inputElement) {
          clearInterval(intervalId);
          this.$nextTick(() => {
            if (window.google && window.google.maps) {
              this.initMap();
            } else {
              console.error("Google Maps JavaScript API library is not loaded.");
            }
          });
        } else if (elapsedTime >= timeout) {
          clearInterval(intervalId);
          console.error(`PlacesAutocomplete: Timed out waiting for input element with ID '${this.inputId}'.`);
        }
      }, checkInterval);
    },

    initMap() {
      if (this.map) return; // Prevent re-initialization

      const inputElement = document.getElementById(this.inputId);
      if (!inputElement) {
        console.error(`PlacesAutocomplete: Input element with ID '${this.inputId}' not found during initMap.`);
        return;
      }

      this.geocoder = new window.google.maps.Geocoder();
      const defaultPosition = { lat: -1.286389, lng: 36.817223 }; // Nairobi

      if (this.initialPosition && this.initialPosition.lat && this.initialPosition.lng) {
        this.setupMapAndMarker(this.initialPosition);
        this.reverseGeocode(this.initialPosition);
        this.initAutocomplete();
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
              const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              };
              this.setupMapAndMarker(pos);
              this.reverseGeocode(pos);
              this.initAutocomplete();
            },
            () => {
              this.setupMapAndMarker(defaultPosition);
              this.initAutocomplete();
            }
        );
      } else {
        this.setupMapAndMarker(defaultPosition);
        this.initAutocomplete();
      }
    },

    setupMapAndMarker(position) {
      this.map = new window.google.maps.Map(this.$refs.map, {
        center: position,
        zoom: 18,
      });

      this.marker = new window.google.maps.Marker({
        position: position,
        map: this.map,
        draggable: true,
      });

      this.marker.addListener('dragend', () => {
        this.reverseGeocode(this.marker.getPosition());
      });
    },

    initAutocomplete() {
      const inputElement = document.getElementById(this.inputId);
      if (!inputElement) {
        console.error(`Input element with ID '${this.inputId}' not found.`);
        return;
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(
          inputElement,
          {
            componentRestrictions: { country: 'ke' },
            fields: ['address_components', 'geometry', 'name', 'formatted_address'],
          }
      );

      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          console.warn("No details available for input: '" + place.name + "'");
          return;
        }

        this.map.setCenter(place.geometry.location);
        this.marker.setPosition(place.geometry.location);
        this.populateFieldsFromPlace(place);
      });
    },

    populateFieldsFromPlace(place) {
      let street = '';
      let country = '';

      for (const component of place.address_components) {
        const componentType = component.types[0];
        if (componentType === 'route') {
          street = component.long_name;
        } else if (componentType === 'country') {
          country = component.long_name;
        }
      }

      const placeDetails = {
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        street: street,
        country: country,
      };

      this.$emit('place-updated', placeDetails);
    },

    reverseGeocode(position) {
      const pos = (position instanceof window.google.maps.LatLng) ? position.toJSON() : position;
      this.geocoder.geocode({ 'location': pos }, (results, status) => {
        if (status === 'OK') {
          if (results[0]) {
            this.populateFieldsFromPlace(results[0]);
          } else {
            console.warn('No results found for reverse geocoding');
          }
        } else {
          console.error('Geocoder failed due to: ' + status);
        }
      });
    },
  },
};
</script>
